import path from 'node:path';
import process from 'node:process';
import opentelemetry, { type Meter, type Tracer } from '@opentelemetry/api';
import logfmt from 'logfmt';
import winston from 'winston';

export type Logger = winston.Logger & {
  tracer: Tracer;
  metrics: Meter;
  getActiveSpan: typeof opentelemetry.trace.getActiveSpan;
};

const formatMessage = (info: winston.Logform.TransformableInfo) =>
  `level=${info.level} ${
    typeof info.message === 'string'
      ? `msg="${info.message?.trim()}"`
      : logfmt.stringify(info.message as object)
  } ${logfmt.stringify(info.metadata as object).replace(/\n/gm, '\\n\\t')}`;

const traceContextFormat = winston.format((info) => {
  const span = opentelemetry.trace.getActiveSpan();
  if (span) {
    const ctx = span.spanContext();
    if (opentelemetry.trace.isSpanContextValid(ctx)) {
      info.trace_id = ctx.traceId;
      info.span_id = ctx.spanId;
    }
  }
  return info;
});

/** The level a runtime override falls back to when it expires or is reset. */
export const BASELINE_LOG_LEVEL = process.env.LOG_LEVEL || 'info';

const logger = winston.createLogger({
  level: BASELINE_LOG_LEVEL,
  format: winston.format.json(),
  levels: winston.config.syslog.levels,
  defaultMeta: {
    service_name: process.env.SERVICE_NAME || 'unknown',
  },
  transports: [
    new winston.transports.Console({
      format: winston.format.combine(
        traceContextFormat(),
        winston.format.metadata(),
        winston.format.printf(formatMessage),
      ),
    }),
  ],
}) as Logger;

logger.warn = logger.warning;
logger.verbose = logger.debug;
logger.http = logger.debug;

/**
 * Changes the level of the root logger. Every createLogger() consumer holds a
 * logger.child() of it and winston resolves the level at write time, so this
 * takes effect everywhere in the process immediately.
 */
export const setRootLogLevel = (level: string): void => {
  if (logger.level === level) return;

  const from = logger.level;
  logger.level = level;
  // ponytail: logged at error severity so the line survives every level the UI
  // offers — otherwise switching to ERROR silences the record of why it went quiet.
  logger.error('LOG_LEVEL_CHANGED', { from, to: level });
};

export const getRootLogLevel = (): string => logger.level;

const SYSLOG_SEVERITY: Record<string, number> = winston.config.syslog.levels;

/**
 * Whether OpenTelemetry spans should be exported at the current log level.
 *
 * Node spans carry no level of their own, so this is a threshold: on at info and
 * debug, off at warning and stricter. Mirrors rust-points, where the `EnvFilter`
 * gates span creation and traces stop below info.
 */
export const isTracingEnabled = (): boolean =>
  (SYSLOG_SEVERITY[logger.level] ?? SYSLOG_SEVERITY.info) >= SYSLOG_SEVERITY.info;

export default (filename: string, metaData: Record<string, string> = {}): Logger => {
  const relPath = filename
    .replace(`${path.dirname(process.cwd())}/`, '')
    // workaround for containerized services
    .replace('/app/services/', '')
    .replace(/\.ts|\.js|\.tsx|\.jsx/, '')
    .split('/');

  const child = logger.child({ module: relPath.join('::'), ...metaData });
  // we create tracer and metrics only, if not in browser
  child.tracer = opentelemetry.trace.getTracer(relPath.join('::'));
  child.getActiveSpan = opentelemetry.trace.getActiveSpan;
  child.metrics = opentelemetry.metrics.getMeter(relPath.join('::'));
  return child;
};

export const getRequestLoggerMiddleware = (logger: winston.Logger, module: string) => {
  logfmt.stream = {
    write: (message: string) => {
      try {
        const msgObj = logfmt.parse(message);
        logger.info('REQUEST', { module, ...msgObj });
      } catch (error) {
        logger.error(error);
      }
    },
  };

  const middleware = logfmt.requestLogger();

  return middleware;
};

export const withNewSpan = <F extends CallableFunction>(
  spanName: string,
  logger: Logger,
  fn: F,
): F => {
  return ((...args: Array<any>) => {
    return logger.tracer.startActiveSpan(spanName, (span) => {
      try {
        return fn(...args);
      } finally {
        span.end();
      }
    });
  }) as unknown as F;
};

export function withNewSpanDec(spanName: string, logger: Logger) {
  return (target: any, _context: ClassMethodDecoratorContext<any>) => {
    return function (this: any, ...args: Array<any>) {
      return logger.tracer.startActiveSpan(spanName, async (span) => {
        try {
          const result = await target.apply(this, args);
          return result;
        } finally {
          span.end();
        }
      });
    };
  };
}
